import type { DividerProps } from './Divider';
import type { DividerOrientation } from '@natter-co/design-system';

type DsProps = {
  orientation: DividerOrientation;
  className?: string;
  testId?: string;
  label?: string;
};

/**
 * Every margin class is spelled out in full on purpose: Tailwind only emits utilities it finds as literal
 * text in a scanned file, so building `my-${n}` at runtime would leave the margin to whichever test or
 * story happens to spell the class. Keys are MUI spacing steps (8px); the classes are Tailwind's (4px).
 */
const MARGIN_CLASSES: Record<string, Record<number, string>> = {
  m: { 0: 'm-0', 0.5: 'm-1', 1: 'm-2', 1.5: 'm-3', 2: 'm-4', 3: 'm-6', 4: 'm-8', 5: 'm-10' },
  mx: { 0: 'mx-0', 0.5: 'mx-1', 1: 'mx-2', 1.5: 'mx-3', 2: 'mx-4', 3: 'mx-6', 4: 'mx-8', 5: 'mx-10' },
  my: { 0: 'my-0', 0.5: 'my-1', 1: 'my-2', 1.5: 'my-3', 2: 'my-4', 3: 'my-6', 4: 'my-8', 5: 'my-10' },
  mt: { 0: 'mt-0', 0.5: 'mt-1', 1: 'mt-2', 1.5: 'mt-3', 2: 'mt-4', 3: 'mt-6', 4: 'mt-8', 5: 'mt-10' },
  mb: { 0: 'mb-0', 0.5: 'mb-1', 1: 'mb-2', 1.5: 'mb-3', 2: 'mb-4', 3: 'mb-6', 4: 'mb-8', 5: 'mb-10' },
  ml: { 0: 'ml-0', 0.5: 'ml-1', 1: 'ml-2', 1.5: 'ml-3', 2: 'ml-4', 3: 'ml-6', 4: 'ml-8', 5: 'ml-10' },
  mr: { 0: 'mr-0', 0.5: 'mr-1', 1: 'mr-2', 1.5: 'mr-3', 2: 'mr-4', 3: 'mr-6', 4: 'mr-8', 5: 'mr-10' },
};

/**
 * The `sx` values that have an exact class: the margin steps in `MARGIN_CLASSES`, `width: '100%'` and
 * `displayPrint: 'none'`. Anything else - a colour, a height, a function, a breakpoint object, a step not
 * in the table - has no exact class and sends the whole divider back to MUI.
 */
const sxToClasses = (sx: DividerProps['sx']): string[] | null => {
  if (sx === undefined) return [];
  if (typeof sx !== 'object' || sx === null || Array.isArray(sx)) return null;

  const classes: string[] = [];

  for (const [key, value] of Object.entries(sx)) {
    const marginClass = typeof value === 'number' ? MARGIN_CLASSES[key]?.[value] : undefined;

    if (marginClass) {
      classes.push(marginClass);
    } else if (key === 'width' && value === '100%') {
      classes.push('w-full');
    } else if (key === 'displayPrint' && value === 'none') {
      classes.push('print:hidden');
    } else {
      return null;
    }
  }

  return classes;
};

/**
 * Maps the legacy MUI `Divider` props onto the Design System 2.0 `Divider`.
 *
 * TODO(HNS-264): temporary, like the other `toDsProps` mappers; retiring the flag deletes it.
 *
 * `null` sends the caller back to MUI. Cases that stay on MUI:
 *   - `children` - rich content; use the plain-text `label` API instead;
 *   - `component`, `variant` (`middle`, `inset`), `light`, `textAlign` - no DS counterpart;
 *   - `sx` with anything but spacing, `width: '100%'` and `displayPrint: 'none'` - in particular a
 *     `borderColor`: the DS rule is a translucent overlay that takes its colour from the theme of the
 *     surface it sits on, so a divider that forces a colour for a fixed dark surface cannot be handed
 *     to it without checking that surface;
 *   - any other prop the DS divider would accept and silently drop.
 *
 * `flexItem` is accepted and dropped: the DS divider already stretches on a flex row and fills a flex
 * column, which is all `flexItem` asked MUI for.
 */
export const toDsProps = ({
  orientation = 'horizontal',
  flexItem: _flexItem,
  className,
  sx,
  'data-testid': testId,
  children,
  label,
  ...unsupported
}: DividerProps): DsProps | null => {
  if (children !== undefined && children !== null && children !== false) return null;
  if (label !== undefined && (orientation !== 'horizontal' || typeof label !== 'string')) return null;
  if (Object.keys(unsupported).length > 0) return null;

  const spacing = sxToClasses(sx);
  if (spacing === null) return null;

  return {
    orientation,
    className: [className, ...spacing].filter(Boolean).join(' ') || undefined,
    testId,
    label,
  };
};
